import { useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";
import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { useNewsroomData } from "@/hooks/useNewsroomData";
import { AdminLivestreamsPage } from "@/pages/AdminLivestreamsPage";
import { AdminSponsorsPage } from "@/pages/AdminSponsorsPage";
import { AdminSubmissionsPage } from "@/pages/AdminSubmissionsPage";
import { AuthorProfilePage } from "@/pages/AuthorProfilePage";
import { BreakingNewsPage } from "@/pages/BreakingNewsPage";
import { ContactPage } from "@/pages/ContactPage";
import { EntityLandingPage } from "@/pages/EntityLandingPage";
import { InvestigationsLandingPage } from "@/pages/InvestigationsLandingPage";
import { LandingPage } from "@/pages/LandingPage";
import { LivePage } from "@/pages/LivePage";
import { LocationHubPage } from "@/pages/LocationHubPage";
import { NewsroomLoginPage } from "@/pages/NewsroomLoginPage";
import { NewsSectionPage } from "@/pages/NewsSectionPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { OfficeUsePage } from "@/pages/OfficeUsePage";
import { SearchPage } from "@/pages/SearchPage";
import { SectionFeedPage } from "@/pages/SectionFeedPage";
import { StaticNewsroomPage } from "@/pages/StaticNewsroomPage";
import { StoryDetailPage } from "@/pages/StoryDetailPage";
import { SubmitTipPage } from "@/pages/SubmitTipPage";
import { TopicLandingPage } from "@/pages/TopicLandingPage";
import { ENTITY_PAGE_LIST } from "@/utils/entityPages";
import { LOCATION_HUB_LIST, NEWSROOM_SECTION_CONFIG } from "@/utils/newsroomCollections";

function App() {
  const { siteData, feed, selectedSource, setSelectedSource, loading, refreshing, refreshNewsroom } = useNewsroomData();
  const [showInstagramPrompt] = useState(false);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-background text-foreground" data-testid="content-watch-app-shell">
        <SiteHeader siteData={siteData} />
        {showInstagramPrompt ? null : null}
        {loading ? (
          <main className="page-shell flex min-h-[70vh] items-center justify-center px-6 py-24" data-testid="app-loading-state">
            <div className="max-w-xl border border-[rgba(10,15,26,0.12)] bg-white px-8 py-10 text-left">
              <p className="eyebrow text-red-600" data-testid="loading-eyebrow">Preparing the newsroom</p>
              <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-slate-950" data-testid="loading-title">Building your live Content Watch experience.</h1>
              <p className="mt-4 text-sm text-slate-600" data-testid="loading-copy">We’re loading the latest feed, sponsor deck, livestream lineup, and newsroom sections.</p>
            </div>
          </main>
        ) : (
          <Routes>
            <Route path="/" element={<LandingPage siteData={siteData} feed={feed} />} />
            <Route path="/breaking-news" element={<BreakingNewsPage siteData={siteData} feed={feed} selectedSource={selectedSource} onSourceChange={setSelectedSource} onRefresh={refreshNewsroom} refreshing={refreshing} />} />
            {Object.values(NEWSROOM_SECTION_CONFIG).filter((section) => section.key !== "investigations").map((section) => (
              <Route key={section.key} path={section.route} element={<NewsSectionPage feed={feed} sectionKey={section.key} />} />
            ))}
            <Route path="/investigations" element={<InvestigationsLandingPage feed={feed} />} />
            {LOCATION_HUB_LIST.map((hub) => (
              <Route key={hub.key} path={hub.route} element={<LocationHubPage feed={feed} hubKey={hub.key} />} />
            ))}
            <Route path="/sports" element={<SectionFeedPage feed={feed} section="sports" />} />
            <Route path="/whats-new" element={<SectionFeedPage feed={feed} section="editorial" />} />
            <Route path="/houston-crime-news" element={<TopicLandingPage feed={feed} topicKey="crime" />} />
            <Route path="/houston-traffic-news" element={<TopicLandingPage feed={feed} topicKey="traffic" />} />
            <Route path="/houston-weather-news" element={<TopicLandingPage feed={feed} topicKey="weather" />} />
            <Route path="/houston-sports-news" element={<TopicLandingPage feed={feed} topicKey="sports" />} />
            <Route path="/houston-neighborhood-news" element={<TopicLandingPage feed={feed} topicKey="neighborhoods" />} />
            {ENTITY_PAGE_LIST.map((entity) => (
              <Route key={entity.key} path={entity.route} element={<EntityLandingPage entityKey={entity.key} feed={feed} />} />
            ))}
            <Route path="/live" element={<LivePage feed={feed} siteData={siteData} />} />
            <Route path="/search" element={<SearchPage feed={feed} />} />
            <Route path="/about" element={<StaticNewsroomPage pageKey="about" />} />
            <Route path="/editorial-standards" element={<StaticNewsroomPage pageKey="editorial-standards" />} />
            <Route path="/corrections-policy" element={<StaticNewsroomPage pageKey="corrections" />} />
            <Route path="/privacy-policy" element={<StaticNewsroomPage pageKey="privacy" />} />
            <Route path="/terms-of-use" element={<StaticNewsroomPage pageKey="terms" />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/submit-a-tip" element={<SubmitTipPage />} />
            <Route path="/authors/:authorSlug" element={<AuthorProfilePage feed={feed} />} />
            <Route path="/other-stories" element={<Navigate replace to="/whats-new" />} />
            <Route path="/newsroom-login" element={<NewsroomLoginPage />} />
            <Route path="/office-use" element={<OfficeUsePage />} />
            <Route path="/admin/sponsors" element={<AdminSponsorsPage />} />
            <Route path="/admin/livestreams" element={<AdminLivestreamsPage />} />
            <Route path="/admin/submissions" element={<AdminSubmissionsPage />} />
            <Route path="/stories/:slug" element={<StoryDetailPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        )}
        {!loading ? <SiteFooter siteData={siteData} /> : null}
        <Toaster richColors position="top-right" />
      </div>
    </BrowserRouter>
  );
}

export default App;
